import React, { useRef, useState, useLayoutEffect, FC } from 'react';
import { Box, Svg, Typography, useThemeValue } from '@altermaind/web/ui';

type Props = {
  icon: string;
  text: string;
  selected?: boolean;
  onClick: () => void;
  ['data-testid']?: string;
};

/**
 * A React functional component that renders a pill-shaped button with an icon and text.
 * The button can be selected or unselected, and it triggers a callback function when clicked.
 * The component dynamically calculates the border radius based on its height and theme padding.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.icon - The name of the icon to display inside the button.
 * @param {string} props.text - The text to display inside the button.
 * @param {boolean} [props.selected=false] - Indicates whether the button is selected. Defaults to `false`.
 * @param {Function} props.onClick - The callback function to execute when the button is clicked.
 *
 * @returns {JSX.Element} The rendered pill button component.
 *
 * @remarks
 * The border radius is dynamically calculated based on the height of the button and the padding value
 * retrieved from the theme. This ensures a consistent pill-shaped appearance across different sizes.
 *
 * @accessibility
 * - Ensure the button has an appropriate `aria-label` or `role` for screen readers.
 * - Avoid relying solely on visual cues (e.g., color) to indicate selection state.
 *
 * @dependencies
 * - React: Used for building the component.
 * - useRef, useState, useLayoutEffect: React hooks for managing state and DOM references.
 * - `@altermaind/web/ui`: Provides UI components like `Box`, `Svg`, `Typography`, and the `useThemeValue` hook.
 * - ResizeObserver: Used to observe size changes of the DOM element.
 *
 * @example
 * ```jsx
 * import React from 'react';
 * import { PillButton } from './PillButton';
 *
 * export const App = () => {
 *   const handleClick = () => {
 *     console.log('Button clicked!');
 *   };
 *
 *   return (
 *     <PillButton
 *       icon="check"
 *       text="Click Me"
 *       selected={false}
 *       onClick={handleClick}
 *     />
 *   );
 * };
 *
 * ```
 */
export const PillButton: FC<Props> = ({
  icon,
  text,
  selected = false,
  onClick,
  'data-testid': dataTestId,
}) => {
  const circleRef = useRef<HTMLDivElement>(null);
  const [radiusPx, setRadiusPx] = useState(0);

  const paddingValue = useThemeValue('space', 'xxs');
  // I convert it to a number (px) for the calculation
  const paddingNumber = parseFloat(`${paddingValue}`) || 0;

  useLayoutEffect(() => {
    const el = circleRef.current;
    let resizeHandler: ResizeObserver | undefined;

    if (el) {
      const updateRadius = () => {
        const { height } = el.getBoundingClientRect();
        // semicircle + padding of the outer Box
        setRadiusPx(height / 2 + paddingNumber);
      };

      updateRadius();
      resizeHandler = new ResizeObserver(updateRadius);
      resizeHandler.observe(el);
    }

    // cleanup: if observer is defined, disconnect it
    return () => {
      if (resizeHandler) {
        resizeHandler.disconnect();
      }
    };
  }, [paddingNumber]);

  return (
    <Box
      clickable={!selected}
      onClick={!selected ? onClick : undefined}
      padding={paddingNumber}
      borderRadius={`${radiusPx}px`}
      bg={selected ? 'primary/interactive' : 'transparent'}
      width='100%'
      display='flex'
      flexDirection='row'
      justifyContent='center'
      alignItems='center'
      data-testid={dataTestId}
      data-selected={selected}
    >
      <Box ref={circleRef} borderRadius='100%' bg='primary/background' padding='xxs' flex={0}>
        <Svg name={icon} size='xs' />
      </Box>
      <Typography variant='p2' flex={1} ml='xxs'>
        {text}
      </Typography>
    </Box>
  );
};
